/**
 * The shell's own screens, as one list (bata-platform#686).
 *
 * The command palette offers them beside the chapters' screens, and the problem report's section
 * picker offers them for the same reason: a person can have something to say about "My work" as
 * much as about a chapter's table. They used to be written out inside the palette; a second
 * hard-coded list in the report form would have drifted from the first the day a shell screen was
 * added, so both read this one.
 *
 * The Cockpit is not here: the palette leaves it out (the rail's mark is its way in, ADR 9004) and
 * the report form adds it, through {@link cockpitTarget}.
 */

import { HOME_PATH } from '../routes/router';
import { ADMIN_USERS_PATH, USER_ACTIVITY_PATH } from '../routes/admin/constants';
import { FEEDBACK_PATH, FEEDBACK_TRIAGE_PATH } from '../routes/feedback/constants';
import { MY_WORK_PATH } from '../routes/my-work/my-work';
import { PROFILE_PATH } from '../routes/profile/ProfileScreen';

export interface ShellTarget {
  readonly to: string;
  readonly labelKey: string;
  /** The group the screen is listed under. */
  readonly chapterLabelKey: string;
}

/**
 * The Cockpit, `/` — in the report form's list and not in the palette's. A function, not a
 * constant: the router imports the screens that import this module, and `HOME_PATH` is only safe
 * to read once both have loaded.
 */
export function cockpitTarget(): ShellTarget {
  return { to: HOME_PATH, labelKey: 'nav.home', chapterLabelKey: 'nav.home' };
}

/** Which of the shell's role-gated screens a person is admitted to (`../feedback/access.ts`). */
export interface ShellTargetAccess {
  /** User management and User activity (ADR 9014, 9059). */
  readonly administers: boolean;
  /** Report triage (ADR 9063), the delivery team's. */
  readonly triages: boolean;
}

/**
 * The shell's screens a person may open. The role-gated ones only where their routes' `requires`
 * would admit the person, so the palette and the report form never offer a screen that answers
 * «you do not have access».
 */
export function shellTargets({ administers, triages }: ShellTargetAccess): readonly ShellTarget[] {
  return [
    // "My work" (ADR 9022): in the rail too, and findable by name.
    { to: MY_WORK_PATH, labelKey: 'nav.myWork', chapterLabelKey: 'nav.account' },
    { to: PROFILE_PATH, labelKey: 'nav.profile', chapterLabelKey: 'nav.account' },
    // "My reports" (bata-platform#686, ADR 9063): what the person has reported, and where it stands.
    { to: FEEDBACK_PATH, labelKey: 'nav.myReports', chapterLabelKey: 'nav.account' },
    ...(triages ? [{ to: FEEDBACK_TRIAGE_PATH, labelKey: 'nav.feedbackTriage', chapterLabelKey: 'nav.account' }] : []),
    ...(administers
      ? [
        { to: ADMIN_USERS_PATH, labelKey: 'nav.userManagement', chapterLabelKey: 'nav.account' },
        { to: USER_ACTIVITY_PATH, labelKey: 'nav.userActivity', chapterLabelKey: 'nav.account' },
      ]
      : []),
  ];
}
